<template>
	<div v-if="!isInEditMode" :class="`has-status has-status--${$getStateColor('decision', decisionState)}`">
		{{ decisionState === 'Null' ? 'Sie haben noch keine Entscheidung getroffen.' : $getStateFullLabel('decision', decisionState, false) }}
	</div>

	<div class="categories" v-if="renderedCategories.length > 0">
		<h3 v-if="!isInEditMode">Kategorien:</h3>

		<div v-for="(category, index) in renderedCategories" :key="index" class="category__item">
			<h4 class="categories__main"> {{ category.main.number }} {{ category.main.name }} </h4>
			<div v-for="(subCategory, subIndex) in category.sub" :key="subIndex" class="category__sub">
				<ul v-if="!isInEditMode">
					<li>
						<span :class="['category-prefix', { 'category-checkmark': subCategory.isLast }]">
							{{ subCategory.prefix }}
						</span>
						{{ subCategory.name }}
					</li>
				</ul>
				<el-checkbox-group v-else v-model="selectedCategories">
					<el-checkbox :value="subCategory" @change="null" v-if="!subCategory.isHead">
						<span class="el-checkbox__label">{{ subCategory.number }} {{ subCategory.name }}</span>
					</el-checkbox>
				</el-checkbox-group>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { CategoryResponse } from '@/types/types';
import { categories } from '@/utils/categories';

export interface CategoryItem {
	number: string;
	name: string;
}

export interface RenderedSubCategory {
	prefix: string;
	name: string;
	isLast: boolean;
	isHead?: boolean;
}

export interface RenderedCategory {
	main: {
		number: string;
		name: string;
	};
	sub: RenderedSubCategory[];
}

export default defineComponent({
	name: 'DetailsUserDecision',
	data() {
		return {
			categories,
			selectedCategories: [] as any[],
		};
	},
	props: {
		decisionCategories: {
			type: Array as () => CategoryResponse[],
			required: true,
		},
		decisionState: {
			type: String,
			required: true,
		},
		isInEditMode: {
			type: Boolean,
			required: false,
			default: false,
		},
	},
	computed: {
		renderedCategories(): any[] {
			if (!this.decisionCategories) return [];

			return this.processCategories(this.decisionCategories);
		},
	},
	methods: {
		processCategories(decisionCategories: CategoryResponse[]): RenderedCategory[] {
			const findCategoryPath = (id: string, categories: any[], path: CategoryItem[] = []): CategoryItem[] | null => {
				for (const category of categories) {
					if (category.id === id) {
						return [...path, { number: category.number, name: category.name }];
					}
					if (category.children) {
						const result = findCategoryPath(id, category.children, [...path, { number: category.number, name: category.name }]);
						if (result) return result;
					}
				}
				return null;
			};

			const output: RenderedCategory[] = [];
			const renderedPaths = new Map<string, RenderedCategory>();

			for (const decisionCategory of decisionCategories) {
				const path = findCategoryPath(decisionCategory.id, this.categories);
				if (path) {
					const mainNumber = path[0].number;
					const pathKey = path.map((item) => item.number).join('-');

					if (!renderedPaths.has(mainNumber)) {
						const newCategory: RenderedCategory = {
							main: {
								number: path[0].number,
								name: path[0].name,
							},
							sub: path.slice(1).map((item, index, array) => ({
								prefix: item.number,
								number: item.number,
								name: item.name,
								isLast: index === array.length - 1,
								isHead: true,
							})),
						};
						output.push(newCategory);
						renderedPaths.set(mainNumber, newCategory);
					} else {
						const existingCategory = renderedPaths.get(mainNumber)!;
						const newSubs = path
							.slice(1)
							.filter((item) => !existingCategory.sub.some((sub) => sub.prefix === item.number))
							.map((item, _, array) => ({
								prefix: item.number,
								number: item.number,
								name: item.name,
								isLast: item.number === decisionCategory.number,
								isHead: true,
							}));
						existingCategory.sub.push(...newSubs);
					}
				}
			}

			output.sort((a, b) => a.main.number.localeCompare(b.main.number));
			output.forEach((category) => {
				category.sub.sort((a, b) => a.prefix.localeCompare(b.prefix));
			});
			output.forEach((category) => {
				category.sub.forEach((sub) => {
					if (sub.isLast) {
						sub.prefix = '✔';
						sub.isHead = false;
					}
				});
			});

			return output;
		},
		setSelectedCategories(categories: CategoryResponse[]) {
			categories.forEach((cat) => {
				this.selectedCategories.push(cat);
			});
		},
	},
	mounted() {
		this.renderedCategories = this.processCategories(this.decisionCategories);
		if (this.isInEditMode) {
			this.renderedCategories[0].sub.forEach((category) => {
				if (!category.isHead) {
					this.selectedCategories.push(category);
				}
			});
		}
	},
	emits: ['update:selectedCategories'],
	watch: {
		selectedCategories: {
			handler() {
				this.$emit('update:selectedCategories', this.selectedCategories);
			},
			deep: true,
		},
	},
});
</script>

<style lang="scss" scoped>
@media screen and (max-width: 390px) {
	.has-status {
		width: auto;
	}
}
</style>
